<template>
	<Modal
		v-model:visible="visible"
		:width="650"
		:title="$t('settings.vrsSettingsForm.rtcmMessagesFilters.' + i18nPrefix + '.header')"
		@ok="onOk"
		@cancel="onCancel"
	>
		<Form
			:model="formState"
			:label-col="{ span: 12 }"
			:wrapper-col="{ span: 12 }"
			:colon="false"
		>
			<FormItem
				:label="$t('settings.vrsSettingsForm.rtcmMessagesFilters.' + i18nPrefix + '.labelLabel')"
				name="label"
				v-bind="validateInfos.label"
			>
				<InputRestricted
					ref="labelField"
					v-model="formState.label"
					:allowedFormatChecker="mountpointChecker"
					:toolTipText="$t('validators.mountpointMessage')"
					@keyup.enter="onOk"
				/>
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.rtcmMessagesFilters.' + i18nPrefix + '.fullMountpointLabel')">
				<div class="pd-sm-x">{{ fullMountpointLabel }}</div>
			</FormItem>
		</Form>
		<template #footer>
			<Button key="back" @click="onCancel">{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.' + i18nPrefix + '.cancelButtonLabel') }}</Button>
			<Button key="submit" type="primary" :loading="isSubmitting" :disabled="!isChanged" @click="onOk">{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.' + i18nPrefix + '.okButtonLabel') }}</Button>
		</template>
	</Modal>
</template>

<script>
import { reactive, ref, nextTick, watch, computed, toRef } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { Modal, Form, FormItem, Button, message } from 'ant-design-vue';

import Api from '@/api/Api';
import Validators from '@/api/Validators';

import { applyFieldErrors, runValidatedSubmit } from '@/components/common/formModal';

import InputRestricted from '@/components/common/InputRestricted';

export default {
	components: {
		Modal, Form, FormItem, InputRestricted, Button,
	},
	props: {
		addMode: Boolean,
		mountpointId: Number,
		mountpointLabel: String,
		baseMountpoint: String,
		networkId: Number, // if networkId == 0 -- NEAR, else -- VRS
		modelValue: Boolean,
		onSuccess: Function,
		fetchSettings: Function,
	},
	emits: ['update:modelValue'],
	setup(props, { emit }) {
		const { t } = useI18n();
		
		const visible = useVModel(props, 'modelValue', emit);

		const formState = reactive({
			label: '',
		});
		
		const {
			resetFields,
			validate,
			validateInfos,
		} = Validators.useAddRtcmfMountpointFormRules(formState);
		
		const isSubmitting = ref(false);
		const labelField = ref(null);
		
		const addModeRef = toRef(props, 'addMode');
		const i18nPrefix = computed(() => addModeRef.value ? 'add-modal' : 'rename-modal');
		const mountpointIdRef = toRef(props, 'mountpointId');
		const mountpointLabelRef = toRef(props, 'mountpointLabel');
		watch(visible, (value) => {
			if (value) {
				// очищаем поля при открытии попапа
				resetFields();
				formState.label = addModeRef.value ? '' : mountpointLabelRef.value;
				isSubmitting.value = false;
				nextTick(() => {
					if (labelField.value?.focus) {
						labelField.value.focus();
					}
				});
			}
		});

		const baseMountpointRef = toRef(props, 'baseMountpoint');
		const fullMountpointLabel = computed(() => {
			return baseMountpointRef.value + '_' + formState.label;
		});

		const isChanged = computed(() => {
			return formState.label !== mountpointLabelRef.value;
		});
		const networkIdRef = toRef(props, 'networkId');
		const onOk = () => {
			if (!isChanged.value) {
				return;
			}
			runValidatedSubmit(validate, isSubmitting, () => {
				Api.rtcmfMountpointCreateRename(addModeRef.value ? 0 : mountpointIdRef.value, formState.label, networkIdRef.value, response => {
					if (response.status === 'ok') {
						message.success(t('settings.vrsSettingsForm.rtcmMessagesFilters.' + i18nPrefix.value + '.success'));
						visible.value = false;
						props.fetchSettings(() => props.onSuccess(response.id));
					} else {
						applyFieldErrors(validateInfos, response.errors, ['label']);
					}
					isSubmitting.value = false;
				});
			});
		};

		const onCancel = () => {
			visible.value = false;
		};

		return {
			visible,
			i18nPrefix,
			formState,
			validateInfos,
			fullMountpointLabel,
			isSubmitting,
			mountpointChecker: Validators.mountpointChecker,
			labelField,
			onOk,
			onCancel,
			isChanged,
		};
	},
};
</script>
